import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, ListItem, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { CodeStoreFileModel } from '../../../api/api';
import { CodeFileViewer } from '../../../components/CodeFileViewer';

interface ContextCodeFileItemProps {
	file: CodeStoreFileModel;
	onDelete?: (fileName: string) => void;
	onRefresh?: (fileName: string) => void;
	defaultExpanded?: boolean;
	alwaysExpanded?: boolean;
}

const ExpandMore = styled((props: { expand: boolean } & React.ComponentProps<typeof IconButton>) => <IconButton {...props} />)(({ theme, expand }) => ({
	color: theme.palette.text.secondary,
	transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest,
	}),
}));

/**
 * Dark‐theme list item with syntax highlighting for C# and horizontal scroll.
 */
const ContextCodeFileItem: React.FC<ContextCodeFileItemProps> = ({ file, onDelete, onRefresh, defaultExpanded = false, alwaysExpanded = false }) => {
	const [expanded, setExpanded] = useState(defaultExpanded || alwaysExpanded);
	const handleToggle = () => {
		if (!alwaysExpanded) {
			setExpanded((prev) => !prev);
		}
	};

	return (
		<Stack direction="column" flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
			<ListItem
				onClick={handleToggle}
				divider
				sx={{
					bgcolor: expanded ? 'background.paper' : 'background.default',
					'&:hover': { bgcolor: alwaysExpanded ? 'background.paper' : 'action.hover' },
					cursor: alwaysExpanded ? 'default' : 'pointer',
					width: '100%',
					maxWidth: '100%',
					overflow: 'hidden',
				}}
			>
				<Grid2 container alignItems="center" sx={{ width: '100%' }}>
					{/* Namespace */}
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" noWrap sx={{ color: 'primary.light' }}>
							{file.namespace}
						</Typography>
					</Grid2>

					{/* File name */}
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" noWrap sx={{ color: 'success.main' }}>
							{file.name}
						</Typography>
					</Grid2>

					{/* Action buttons */}
					<Grid2 size={{ xs: alwaysExpanded ? 4 : 1 }}>
						<Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
							{onRefresh && (
								<Tooltip title="Refresh file">
									<IconButton
										aria-label="refresh file"
										size="small"
										color="info"
										onClick={(e) => {
											e.stopPropagation();
											onRefresh(file.name);
										}}
									>
										<RefreshIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							{onDelete && (
								<Tooltip title="Delete file">
									<IconButton
										aria-label="delete file"
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											onDelete(file.name);
										}}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
						</Stack>
					</Grid2>

					{/* Toggle */}
					{!alwaysExpanded && (
						<Grid2 size={{ xs: 3 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
							<ExpandMore expand={expanded} aria-expanded={expanded} aria-label="show code">
								<ExpandMoreIcon />
							</ExpandMore>
						</Grid2>
					)}
				</Grid2>
			</ListItem>

			{expanded && (
				<Box
					sx={{
						borderLeft: '4px solid',
						borderColor: 'primary.main',
						display: 'flex',
						flex: 1,
						flexDirection: 'column',
						minHeight: 0,
						overflow: 'hidden',
					}}
				>
					<CodeFileViewer path={file.name ?? 'source.cs'} content={file.content} language="csharp" showHeader={false} />
				</Box>
			)}
		</Stack>
	);
};

export default ContextCodeFileItem;
